import React, { useEffect, useState } from 'react';
import {
    BodySmallEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Emphasized,
    H6Emphasized,
    Input,
    ScrollView,
    Spinner,
    SpinnerSize,
    SSNInput,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { getPersonDisplayName } from '../../common/utils/formatting';
import { useCreateLnp } from '../hooks/useLnpData';
import type { InterventionProfile } from '../types/InterventionTypes';
import type { AdminCreateLnpRequest } from '../types/LnpTypes';

const initialFormValues = () => ({
    userId: '',
    ssn: '',
    shareholderOfPublicCompany: '',
    stockExchangeMember: '',
    boardOfPublicCompany: '',
    politicalFamilyNames: '',
    politicalOrganization: '',
});

type FormValues = ReturnType<typeof initialFormValues>;

export interface CreateLnpModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
    selectedProfile: InterventionProfile | null;
}

const Field = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <YStack gap={4} width="100%">
        <BodySmallEmphasized>{label}</BodySmallEmphasized>
        {children}
    </YStack>
);

const CreateLnpForm = ({
    isOpen,
    onClose,
    onCreated,
    selectedProfile,
}: CreateLnpModalProps) => {
    const toast = useToastController();
    const { mutateAsync: createLnp, isPending } = useCreateLnp();
    const [form, setForm] = useState<FormValues>(initialFormValues);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setForm({
            ...initialFormValues(),
            userId: selectedProfile?.userId ?? '',
        });
    }, [isOpen, selectedProfile]);

    const handleSubmit = async () => {
        setIsConfirmModalOpen(false);

        const request: AdminCreateLnpRequest = {
            userId: form.userId.trim(),
            ssn: form.ssn.replace(/\D/g, ''),
        };

        if (form.shareholderOfPublicCompany.trim())
            request.shareholderOfPublicCompany =
                form.shareholderOfPublicCompany.trim();
        if (form.stockExchangeMember.trim())
            request.stockExchangeMember = form.stockExchangeMember.trim();
        if (form.boardOfPublicCompany.trim())
            request.boardOfPublicCompany = form.boardOfPublicCompany.trim();

        const familyNames = form.politicalFamilyNames
            .split(/[\s,]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        const org = form.politicalOrganization.trim();
        if (familyNames.length > 0 || org) {
            request.politicalExposure = {
                ...(familyNames.length > 0 && { familyNames }),
                ...(org && { organization: org }),
            };
        }

        try {
            const result = await createLnp({ body: request });
            toast.show(
                `LNP created: ${result.apexLegalNaturalPersonId}`,
                MutationSuccessToastOptions,
            );
            onCreated();
            onClose();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Unknown error';
            toast.show(
                `Failed to create LNP: ${message}`,
                MutationErrorToastOptions(),
            );
        }
    };

    const memberDisplayName =
        selectedProfile != null
            ? getPersonDisplayName({
                  firstName:
                      selectedProfile.user.legalFirstName ??
                      selectedProfile.user.firstName,
                  lastName:
                      selectedProfile.user.legalLastName ??
                      selectedProfile.user.lastName,
                  email: selectedProfile.user.email,
              })
            : null;

    // When a profile is selected, only show override fields that are relevant to
    // that member's data.
    const {
        shareholderOfPublicCompany = null,
        stockExchangeMember = null,
        boardOfPublicCompany = null,
        politicalExposure = null,
    } = selectedProfile ?? {};
    const noProfile = selectedProfile == null;
    const showShareholder = noProfile || shareholderOfPublicCompany === 'YES';
    const showStockExchangeMember = noProfile || stockExchangeMember === 'YES';
    const showBoard = noProfile || boardOfPublicCompany === 'YES';
    const showPoliticalExposure = noProfile || politicalExposure === 'YES';
    const hasAnyOptionalOverride =
        showShareholder ||
        showStockExchangeMember ||
        showBoard ||
        showPoliticalExposure;

    return (
        <>
            <YStack
                width={600}
                maxWidth="100%"
                maxHeight="80vh"
                flex={1}
                overflow="hidden"
            >
                <ScrollView flex={1} minHeight={0}>
                    <YStack
                        gap={20}
                        width="100%"
                        padding={20}
                        paddingBottom={8}
                    >
                        <YStack gap={4}>
                            <H5Emphasized>
                                Create Legal Natural Person
                            </H5Emphasized>
                            {memberDisplayName != null && (
                                <H6Emphasized color="$gray1000">
                                    {memberDisplayName}
                                </H6Emphasized>
                            )}
                        </YStack>

                        <YStack gap={16} width="100%">
                            <BodySmallEmphasized color="$gray800">
                                Required
                            </BodySmallEmphasized>
                            <Field label="User ID">
                                <Input
                                    size="tiny"
                                    value={form.userId}
                                    onChangeText={(v) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            userId: v,
                                        }));
                                    }}
                                    disabled
                                />
                            </Field>
                            <Field label="SSN">
                                <SSNInput
                                    size="tiny"
                                    value={form.ssn}
                                    onChangeText={(v) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            ssn: v,
                                        }));
                                    }}
                                    placeholder="XXX-XX-XXXX"
                                    showValue
                                    allowToggle={false}
                                />
                            </Field>
                        </YStack>

                        {hasAnyOptionalOverride && (
                            <YStack gap={16} width="100%">
                                <BodySmallEmphasized color="$gray800">
                                    Compliance overrides
                                </BodySmallEmphasized>
                                {showShareholder && (
                                    <Field label="Shareholder (ticker)">
                                        <Input
                                            size="tiny"
                                            value={
                                                form.shareholderOfPublicCompany
                                            }
                                            onChangeText={(v) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    shareholderOfPublicCompany:
                                                        v,
                                                }));
                                            }}
                                            placeholder="e.g. AAPL"
                                        />
                                    </Field>
                                )}
                                {showStockExchangeMember && (
                                    <Field label="Exchange member">
                                        <Input
                                            size="tiny"
                                            value={form.stockExchangeMember}
                                            onChangeText={(v) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    stockExchangeMember: v,
                                                }));
                                            }}
                                            placeholder="Entity name"
                                        />
                                    </Field>
                                )}
                                {showBoard && (
                                    <Field label="Board (ticker)">
                                        <Input
                                            size="tiny"
                                            value={form.boardOfPublicCompany}
                                            onChangeText={(v) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    boardOfPublicCompany: v,
                                                }));
                                            }}
                                            placeholder="e.g. GOOG"
                                        />
                                    </Field>
                                )}
                                {showPoliticalExposure && (
                                    <>
                                        <Field label="Politically Exposed Organization">
                                            <Input
                                                size="tiny"
                                                value={
                                                    form.politicalOrganization
                                                }
                                                onChangeText={(v) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        politicalOrganization:
                                                            v,
                                                    }));
                                                }}
                                                placeholder="Organization name"
                                            />
                                        </Field>
                                        <Field label="Politically Exposed Family Names (comma-separated)">
                                            <Input
                                                size="tiny"
                                                value={
                                                    form.politicalFamilyNames
                                                }
                                                onChangeText={(v) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        politicalFamilyNames: v,
                                                    }));
                                                }}
                                                placeholder="Name1, Name2"
                                            />
                                        </Field>
                                    </>
                                )}
                            </YStack>
                        )}
                    </YStack>
                </ScrollView>

                <XStack
                    gap={12}
                    padding={16}
                    paddingTop={12}
                    borderTopWidth={1}
                    borderTopColor="$gray200"
                    flexShrink={0}
                >
                    <Button
                        flex={1}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        text="Cancel"
                        onPress={onClose}
                    />
                    <Button
                        flex={1}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        text={isPending ? 'Creating…' : 'Create LNP'}
                        icon={
                            isPending ? (
                                <Spinner size={SpinnerSize.SMALL} />
                            ) : undefined
                        }
                        iconPosition="left"
                        disabled={
                            form.userId.trim() === '' ||
                            form.ssn.trim() === '' ||
                            isPending
                        }
                        onPress={() => {
                            setIsConfirmModalOpen(true);
                        }}
                    />
                </XStack>
            </YStack>

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                onConfirm={handleSubmit}
                description={`Create an Apex LNP for user ${form.userId}? This action cannot be undone.`}
                yesText="Create LNP"
                noText="Cancel"
            />
        </>
    );
};

export const CreateLnpModal = (props: CreateLnpModalProps) => (
    <GroveSimpleModal
        isOpen={props.isOpen}
        onClose={props.onClose}
        hasCloseButton
    >
        <CreateLnpForm {...props} />
    </GroveSimpleModal>
);
